import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { inject, Signal } from '@angular/core';
import { BcLicenseSummary, CspSubscription, CspSubscriptionsQueryParams } from '@models/csp/subscription.model';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { catchError, EMPTY, pipe, switchMap, tap } from 'rxjs';
import { createCustomStateSetOnRegexHandler, getAllWithParams, guidRegex, StateDictionary } from '../../helpers';
import { CspSubscriptionsService } from './subscriptions.service';

type CspSubscriptionsState = {
  cspSubscriptions: CspSubscription[];
  cspSubscriptionsLoading: boolean;
  bcLicenseCounts: BcLicenseSummary[];
  bcLicenseCountsLoading: boolean;
};

const initialState: CspSubscriptionsState = {
  cspSubscriptions: [],
  cspSubscriptionsLoading: false,
  bcLicenseCounts: [],
  bcLicenseCountsLoading: false,
};

const cspSubscriptionsDictionary: StateDictionary<CspSubscription> = {
  entityList: 'cspSubscriptions',
  listLoading: 'cspSubscriptionsLoading',
  entityId: 'subscriptionId',
  // unused
  createLoading: '',
  selectedEntity: '',
  selectedEntityLoading: '',
};

export const CspSubscriptionsStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withDevtools('csp-subscriptions-store'),
  withMethods((store, cspSubscriptionsService = inject(CspSubscriptionsService)) => {
    createCustomStateSetOnRegexHandler<CspSubscriptionsState>(new RegExp(`^/(customers|partners)/${guidRegex}/csp-subscriptions$`))(store, {
      cspSubscriptions: [],
    });
    createCustomStateSetOnRegexHandler<CspSubscriptionsState>(new RegExp(`^/customers/${guidRegex}/bc-license-summary$`))(store, {
      bcLicenseCounts: [],
    });

    const repositoryMethods = {
      getCspSubscriptions: getAllWithParams<CspSubscription, CspSubscriptionsQueryParams>({
        store,
        service: cspSubscriptionsService,
        dictionary: cspSubscriptionsDictionary,
      }),

      getBcLicenseSummary: rxMethod<string>(
        pipe(
          tap(() => patchState(store, { bcLicenseCountsLoading: true, bcLicenseCounts: [] })),
          switchMap(customerTenantId =>
            cspSubscriptionsService.getBcLicenseSummary(customerTenantId).pipe(
              tap(bcLicenseCounts => patchState(store, { bcLicenseCounts, bcLicenseCountsLoading: false })),
              catchError(() => {
                patchState(store, { bcLicenseCounts: [], bcLicenseCountsLoading: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),
    };

    return {
      ...repositoryMethods,
    };
  }),
);

export interface CspSubscriptionsStoreType {
  readonly cspSubscriptions: Signal<CspSubscription[]>;
  readonly cspSubscriptionsLoading: Signal<boolean>;
  readonly bcLicenseCounts: Signal<BcLicenseSummary[]>;
  readonly bcLicenseCountsLoading: Signal<boolean>;

  readonly getCspSubscriptions: (params: CspSubscriptionsQueryParams) => void;
  readonly getBcLicenseSummary: (customerTenantId: string) => void;
}
